import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ShieldCheck,
  Layers,
  User,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Tag,
  ShieldAlert,
} from 'lucide-react';
import {
  formatPaymentCurrency,
  formatPaymentDate,
  PaymentMethodIcon,
  RouteLogo,
  RouteOIcon,
  PageLoader,
} from '@repo/ui';

// ── Validation ────────────────────────────────────────────────────────────────
// BIL-YYYY-NNN format (e.g. BIL-2026-2851) — prevents path traversal / malformed requests
const BILL_NUMBER_RE = /^BIL-\d{4}-\d+$/i;

// ── Reusable meta field ────────────────────────────────────────────────────────
const MetaField = ({
  icon,
  label,
  children,
  fullWidth = false,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <div className={`space-y-2 ${fullWidth ? 'col-span-2' : ''}`}>
    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.18em] flex items-center gap-1.5">
      <span className="opacity-70">{icon}</span>
      {label}
    </p>
    {children}
  </div>
);

// ── VerifyBillPage ────────────────────────────────────────────────────────────

const VerifyBillPage = () => {
  const { t, i18n } = useTranslation();
  const { billNumber } = useParams<{ billNumber: string }>();
  const [searchParams] = useSearchParams();
  const lang = i18n.language;

  const sig = searchParams.get('sig') ?? '';
  const isValidFormat = BILL_NUMBER_RE.test(billNumber ?? '');

  const {
    data: bill,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['public-bill-verify', billNumber, sig],
    queryFn: () =>
      api
        .get(`/student/payments/bills/public/${billNumber}`, { params: { sig } })
        .then((res) => res.data?.data),
    enabled: !!billNumber && isValidFormat && !!sig,
    retry: (failureCount, err: any) => {
      const status = err?.response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
    staleTime: 1000 * 60 * 5,
  });

  // ── Shared error/invalid screen ───────────────────────────────────────────
  const StateScreen = ({
    icon,
    iconBg,
    title,
    subtitle,
  }: {
    icon: React.ReactNode;
    iconBg: string;
    title: string;
    subtitle: string;
  }) => (
    <div className="flex flex-col items-center justify-center min-h-[100svh] bg-[#f8fafc] px-6 py-12 text-center gap-6">
      <div className={`h-20 w-20 sm:h-24 sm:w-24 rounded-full ${iconBg} flex items-center justify-center shadow-sm`}>
        {icon}
      </div>
      <div className="space-y-2 max-w-xs">
        <h1 className="text-xl sm:text-2xl font-black text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 font-medium leading-relaxed">{subtitle}</p>
      </div>
      <a
        href="https://routelms.com"
        className="px-8 py-3 rounded-2xl bg-[#002c8d] text-white font-black text-sm shadow-lg shadow-[#002c8d]/20 active:scale-95 transition-all"
      >
        {t('student.receipt.verify.returnHome')}
      </a>
    </div>
  );

  // ── Invalid URL format ─────────────────────────────────────────────────────
  if (!isValidFormat || !sig) {
    return (
      <StateScreen
        icon={<ShieldAlert className="h-10 w-10 sm:h-12 sm:w-12 text-amber-500" />}
        iconBg="bg-amber-50 border border-amber-100"
        title={t('student.receipt.verify.invalidTitle')}
        subtitle={t('student.receipt.verify.invalidSubtitle')}
      />
    );
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <PageLoader
        message={t('student.receipt.verify.verifying')}
        fullscreen
        className="bg-[#f8fafc]"
      />
    );
  }

  // ── Error (403 = tampered sig, 404 = not found) ───────────────────────────
  if (isError || !bill) {
    const isForbidden = (error as any)?.response?.status === 403;
    return (
      <StateScreen
        icon={<XCircle className="h-10 w-10 sm:h-12 sm:w-12 text-rose-500" />}
        iconBg="bg-rose-50 border border-rose-100"
        title={t('student.receipt.verify.invalidTitle')}
        subtitle={
          isForbidden
            ? t('student.receipt.verify.tamperedSubtitle', {
                defaultValue: 'رابط التحقق غير صالح. يرجى مسح الـ QR Code الأصلي.',
              })
            : t('student.receipt.verify.invalidSubtitle')
        }
      />
    );
  }

  const paymentDate = bill.createdAt ?? bill.date ?? bill.paidAt;

  return (
    <div className="min-h-[100svh] bg-[#f8fafc] py-6 sm:py-10 px-4 sm:px-6 overflow-x-hidden">
      <div className="max-w-lg mx-auto space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-700">

        {/* ── Verification Banner ─────────────────────────────────────────── */}
        <div className="flex items-center justify-center gap-2.5 py-3.5 px-5 bg-[#e9f7ef] border-2 border-[#c3ead4] rounded-[24px] text-[#1e9e55] shadow-sm">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <span className="text-sm sm:text-base font-black uppercase tracking-wide">
            {t('student.receipt.verify.verifiedBanner')}
          </span>
        </div>

        {/* ── Main Receipt Card ───────────────────────────────────────────── */}
        <div className="rounded-[32px] sm:rounded-[40px] border border-slate-200 bg-white shadow-[0_20px_60px_-12px_rgba(0,0,0,0.12)] overflow-hidden">

          {/* Card Header */}
          <div className="bg-[#002c8d] px-6 py-7 sm:px-10 sm:py-9 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 h-48 w-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="relative flex flex-row items-center justify-between gap-4">
              {/* Brand */}
              <div className="space-y-1">
                <div className="h-9 sm:h-11 flex items-center">
                  <RouteLogo className="h-full w-auto fill-white" />
                </div>
                <p className="text-white/70 text-[10px] font-black uppercase tracking-[0.18em] mt-2">
                  {t('student.receipt.verify.title')}
                </p>
              </div>
              {/* Icon */}
              <div className="h-14 w-14 sm:h-16 sm:w-16 shrink-0 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20 p-3 shadow-lg">
                <RouteOIcon className="h-full w-full text-white fill-current" />
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="px-5 py-7 sm:px-8 sm:py-10 space-y-8">

            {/* Amount */}
            <div className="text-center space-y-2 py-7 sm:py-9 bg-slate-50/70 rounded-[24px] sm:rounded-[32px] border border-slate-100">
              <p className="text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                {t('student.receipt.verify.registeredAmount')}
              </p>
              <p className="text-5xl sm:text-6xl font-black text-[#1e9e55] tabular-nums leading-none">
                {formatPaymentCurrency(bill.amount, lang)}
              </p>
            </div>

            {/* Metadata Grid — 2 cols on sm+, 1 col on xs */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-7 sm:gap-x-10 sm:gap-y-9">

              {/* Receipt Ref */}
              <MetaField
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                label={t('student.receipt.verify.receiptRef')}
              >
                <p className="text-base sm:text-lg font-black font-mono text-slate-900 tracking-wider break-all">
                  {bill.number ?? billNumber}
                </p>
              </MetaField>

              {/* Payment Method */}
              <MetaField
                icon={<PaymentMethodIcon method={bill.paymentMethod} className="h-3.5 w-3.5" />}
                label={t('student.receipt.detail.paymentMethod')}
              >
                <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-slate-50 border border-slate-100 w-fit">
                  <div className="h-8 w-8 rounded-xl bg-white flex items-center justify-center shadow-sm text-slate-600 shrink-0">
                    <PaymentMethodIcon method={bill.paymentMethod} />
                  </div>
                  <span className="text-sm sm:text-base font-black text-slate-900">
                    {t(`common.${bill.paymentMethod?.toLowerCase()}`, {
                      defaultValue: bill.paymentMethod,
                    })}
                  </span>
                </div>
              </MetaField>

              {/* Student Name */}
              <MetaField
                icon={<User className="h-3.5 w-3.5" />}
                label={t('student.receipt.verify.student')}
              >
                <p className="text-base sm:text-lg font-black text-slate-900">
                  {bill.student?.name ?? '---'}
                </p>
              </MetaField>

              {/* Payment Date */}
              <MetaField
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label={t('student.receipt.verify.paymentDate')}
              >
                <p className="text-sm sm:text-base font-bold text-slate-900 leading-snug">
                  {formatPaymentDate(paymentDate, lang)}
                </p>
              </MetaField>

              {/* Program — full width */}
              {bill.program?.trackName && (
                <MetaField
                  icon={<Layers className="h-3.5 w-3.5" />}
                  label={t('student.receipt.verify.program')}
                  fullWidth
                >
                  <p className="text-base sm:text-lg font-black text-slate-900 leading-snug">
                    {bill.program.trackName}
                  </p>
                </MetaField>
              )}
            </div>

            {/* Footer */}
            <div className="pt-6 border-t border-dashed border-slate-100 text-center space-y-2">
              <p className="text-xs text-slate-400 font-semibold leading-relaxed px-2">
                {t('student.receipt.verify.trustFooter')}
              </p>
              <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-300 font-black uppercase tracking-widest">
                <Tag className="h-2.5 w-2.5" />
                {t('student.receipt.verify.verifiedOn', {
                  date: new Date().toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US'),
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Branding Footer */}
        <div className="text-center pb-4 opacity-25 hover:opacity-100 transition-opacity duration-500">
          <RouteLogo className="h-5 w-auto fill-slate-800 mx-auto" />
          <p className="text-[9px] font-black text-slate-700 uppercase tracking-[0.4em] mt-1.5">
            Route Academy &copy; 2026
          </p>
        </div>

      </div>
    </div>
  );
};

export default VerifyBillPage;
